
import { NavigateOptions } from "react-router-dom";

// Type definition for the redirect reason/source
export type RedirectReason = 
  | 'auth' 
  | 'profile' 
  | 'tos' 
  | 'booking' 
  | 'vendor' 
  | 'admin' 
  | 'session_expired'
  | 'recovery'
  | 'signup'
  | 'other';

// Options for navigation
export interface NavigationOptions extends NavigateOptions {
  delay?: number;
  reason?: RedirectReason;
  forceReplace?: boolean;
}

// Store the original path when redirecting
export const saveCurrentPath = (excludePaths: string[] = ['/auth', '/terms-of-service']) => {
  const currentPath = window.location.pathname + window.location.search;
  // Don't save excluded paths
  if (!excludePaths.some(path => currentPath.startsWith(path))) {
    sessionStorage.setItem("originalPath", currentPath);
    console.log(`[NavigationService] Saved original path: ${currentPath}`);
  }
};

// Get the original path to return to after completing a flow
export const getOriginalPath = (defaultPath = '/'): string => {
  const savedPath = sessionStorage.getItem("originalPath");
  console.log(`[NavigationService] Retrieved original path: ${savedPath || defaultPath}`);
  // Clear the saved path after retrieving it
  sessionStorage.removeItem("originalPath");
  return savedPath || defaultPath;
};

// Centralized navigation helper to standardize navigation behavior
export const navigateTo = (
  navigate: (path: string, options?: NavigateOptions) => void,
  path: string,
  options: NavigationOptions = {}
) => {
  const { delay = 0, reason, forceReplace = false, ...routerOptions } = options;
  
  // For certain paths, always use replace: true to prevent back button issues
  const alwaysReplacePaths = ['/auth', '/terms-of-service', '/setup-profile'];
  const shouldReplace = 
    forceReplace || 
    routerOptions.replace || 
    alwaysReplacePaths.some(p => path.startsWith(p));
  
  // Log navigation for debugging
  const timestamp = new Date().toISOString();
  console.log(`[NavigationService] Navigating to: ${path}${reason ? ` (reason: ${reason})` : ''}${shouldReplace ? ' [replacing]' : ''}${delay > 0 ? ` with ${delay}ms delay` : ''} (timestamp: ${timestamp})`);
  
  // Perform the navigation with or without delay
  if (delay > 0) {
    setTimeout(() => {
      console.log(`[NavigationService] Executing delayed navigation to ${path} (timestamp: ${new Date().toISOString()})`);
      navigate(path, { ...routerOptions, replace: shouldReplace });
    }, delay);
  } else {
    navigate(path, { ...routerOptions, replace: shouldReplace });
  }
};

// Special case handlers for common navigation patterns
export const navigateToTerms = (navigate: (path: string, options?: NavigateOptions) => void, options: NavigationOptions = {}) => {
  saveCurrentPath();
  navigateTo(navigate, '/terms-of-service', { ...options, forceReplace: true, reason: 'tos' });
};

export const navigateToProfile = (navigate: (path: string, options?: NavigateOptions) => void, options: NavigationOptions = {}) => {
  saveCurrentPath();
  navigateTo(navigate, '/my-profile', { ...options, reason: 'profile' });
};

export const navigateToAuth = (navigate: (path: string, options?: NavigateOptions) => void, options: NavigationOptions = {}) => {
  saveCurrentPath();
  navigateTo(navigate, '/auth', { ...options, forceReplace: true, reason: 'auth' });
};

export const navigateAfterFlow = (navigate: (path: string, options?: NavigateOptions) => void, options: NavigationOptions = {}) => {
  const path = getOriginalPath();
  navigateTo(navigate, path, { ...options, forceReplace: true });
};
